import React, { useState, useEffect } from 'react';
import { List, Icon, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import DocumentListItem from './DocumentListItem';
import withInfiniteScroll from 'global/components/withInfiniteScroll';
import { get, set, uniq, flatten, size, merge, orderBy, first } from 'global/lib/lodash';
import useStyles from './DocumentList.styles';
import useRetrieveData from 'global/hooks/useRetrieveData';
import sendData from 'redux/actions/sendData';
import useDebounce from 'global/hooks/useDebounce';
import { Loader, useScreen } from 'global/styles';
import withSearchBar from 'global/components/withSearchBar';
import { CenterContent, LeftSideBar, RightSideBar, GridContainer } from 'global/components/perform-grid';
import LibraryFilters from '../Filters/LibraryFilters';
import { stringSearch } from 'global/utils';
import clsx from 'clsx';
import { FILEADD, getIcon, VIDEOADD, INDOC } from '../definitions';
import arrayToTree from 'array-to-tree';
import { PrintView } from 'global/components/print';
import DocumentContainer from '../DocumentContainer';
import { flattenAllChildren } from 'global/utils';
import { SelectLangEditBar } from 'global/components/lang';
import { FilterChips } from 'global/components/filterchips';
import { useEdit } from 'global/hooks/useEdit';
import { useFilter } from 'global/hooks/useFilter';
import { useUser } from 'global/hooks/useUser';
import { useLocation } from 'react-router-dom';
import { getUrlParams } from 'global/utils';
import { useShowChanges } from 'global/hooks/useShowChanges';
import { Column } from 'global/components/row';
import { Text } from 'global/components/text';
import withDocumentParent from '../Document/withDocumentParent';
import LibraryIcon from '@mui/icons-material/Book';

export const Includes = ['document', 'classification', 'classification.system', 'classification.assembly', 'classification.material', 'classification.material.parent'];

export const Transform = (da) => {
  if (Array.isArray(da)) {
    return da.map((d) => ({ ...d, document: { ...(d.document || {}), classification: d.classification || {} } }));
  } else {
    return da;
  }
};

const DocumentContainerWrapped = withDocumentParent(DocumentContainer);

const DocumentList = (props) => {
  const { isDocumentPath } = props;
  const [data, _setData] = useState([]);
  const [sideFilters, setSideFilters] = useState({});
  const [searchString, setSearchString] = useState('');
  const [wallterResults, setWallterResults] = useState([]);
  const [selected, setSelected] = useState({});
  const [printItems, setPrintItems] = useState([]);
  const libid = get(props, 'match.params.library', get(props, 'libid', 0));
  const debouncedSearch = useDebounce(searchString, 800);
  const { isMobile } = useScreen();
  const classes = useStyles({ isMobile });
  const { editOn } = useEdit();
  const { showChangesOn, toggleShowChanges } = useShowChanges();
  const { filter, canShowDoc } = useFilter('library.editions');
  const { siteContextSelectedRealm } = useUser();
  const routerLocation = useLocation();
  const { userBuilderRealms } = useUser();
  const userBuilderAccess = userBuilderRealms.map(b => b.id);

  const locationOn = get(filter, 'useLocation', false)

  const edition = useRetrieveData({ url: `/editions/${libid}` }, { build: ['editions'], params: { include: 'realm' } });
  const editionDocuments = useRetrieveData(
    { url: `/edition-documents` },
    {
      useLangFilter: true,
      useEffect: true,
      build: ['editionDocuments'],
      params: { include: Includes.join(','), filter: { edition_id: libid } },
      transformData: Transform,
      useCache: editOn ? false : { reload: 20 },
    }
  );

  const editionParentId = get(edition,'data.0.parentId');
  const canShowChanges = !!editionParentId && userBuilderAccess.includes(get(edition,'data.0.ownerRealmId').toString())

  const editionParent = useRetrieveData({ url: `/editions/${editionParentId}` }, { build: ['editions'], waitFor: canShowChanges && showChangesOn && !!editionParentId, useEffect: true, useCache: { reload: 20 } });

  useEffect(() => {
    if (!edition.loading) {
      if (showChangesOn && !canShowChanges) {
        // If a user enables "Show Changes" on an Edition where they have access to it,
        // but then switch to an Edition where they don't have access, turn it off.
        toggleShowChanges();
      } else if (!showChangesOn && canShowChanges && !!get(getUrlParams(routerLocation.search),'documentParentId',false)) {
        // If a user opens a url link to a document that has "documentParentId" in the URL,
        // (and they have access to show changes) turn on the Show Changes
        toggleShowChanges();
      }
    }
  },[canShowChanges,edition.loading])

  const editionParentDocuments = useRetrieveData(
    { url: `/edition-documents` },
    {
      useLangFilter: true,
      useEffect: true,
      build: ['editionDocuments'],
      params: { filter: { edition_id: editionParentId } },
      waitFor: !!editionParentId && showChangesOn,
      transformData: Transform,
      useCache: { reload: 20 },
    }
  );

  // If a user toggles "Show Changes" from within a document view, we need to find the parent document and save it's id
  // in the view that's accessible to the document -- this prevents the site from doing double work where it might otherwise
  // have to load the entire 
  useEffect(() => {
    if (routerLocation.pathname.includes("document") &&
        showChangesOn &&
        editionDocuments.loading === false &&
        editionParentDocuments.loading === false ) {
      const params = getUrlParams(routerLocation.search);
      const editionDocumentId = get(params,'activeTab','');
      let match = {};
      if (editionDocumentId) {
        // if user has selected an alternate tab from the "Primary" document
        match = data.reduce((agg,c) => {
          if (agg !== undefined) { return agg }
          return c.find((item) => get(item,"id") === editionDocumentId)
        },undefined);
      } else {
        // if not, use classificationId and primaryDocument to find tab
        const classificationId = routerLocation.pathname.split("/").pop()
        const matches = data.find((c) => get(c,"0.classificationId").toString() === classificationId);
        match = matches.find((item) => get(item,'primaryDocument') === 1) || matches[0]
      }
      const { documentParentInParentEdition } = match;
      if (!!documentParentInParentEdition && get(documentParentInParentEdition,'documentableId') !== get(params,'documentParentId','')) {
        // set a new documentable id
        toggleShowChanges(get(documentParentInParentEdition,'documentableId'));
      } else {
        toggleShowChanges(false,false);
      }
    }
  }, [routerLocation, data, showChangesOn])

  const getNewList = () => {
    const classMap = {};
    const items = editionDocuments.data;
    const parentItems = editionParentDocuments?.data;
    const parentIsNotLoaded = size(parentItems) < 1 || editionParentDocuments.loading
    items.map((cl) => {
      const id = get(cl, 'document.classification.id');
      const documentExistsInParentEdition = parentIsNotLoaded || parentItems.some((i) => 
        get(i,'documentableId') === get(cl,'documentableId') &&
        get(i,'documentableType') === get(cl,'documentableType')
      );
      const documentParentInParentEdition = parentIsNotLoaded ? false : first(parentItems.filter((i) => 
        get(i,'documentableId') === get(cl,'document.parentId') && 
        get(i,'documentableType') === get(cl,'documentableType'))
      );
      const documentParentExistsInParentEdition = !!documentParentInParentEdition;
      if (id) {
        set(classMap, id, orderBy([...get(classMap, id, []), {...cl,documentExistsInParentEdition,documentParentExistsInParentEdition,documentParentInParentEdition}], ['primaryDocument', 'order', 'name'], ['desc', 'asc', 'asc']));
      }
    });

    return classMap;
  };

  const getChecklists = (flat = false) => {
    const classMap = getNewList();

    return flat ? flatten(Object.keys(classMap).map((k) => classMap[k])) : Object.keys(classMap).map((k) => classMap[k]);
  };

  const setData = (da) => {
    const map = {};
    da.map((da) => {
      const sysid = get(da, '0.document.classification.id');
      set(map, sysid, orderBy([...get(map, sysid, []), da], '0.order'));
    });

    const flat = flatten(Object.keys(map).map((k) => map[k]));
    const sorted = orderBy(flat, '0.order')
      .map((f) => (locationOn ? f.filter((ff) => canShowDoc(ff, first(edition.data))) : f))
      .filter((f) => size(f));

    _setData(sorted);
  };

  const generateSidebar = (vals = {}) => {
    const obj = merge(
      {},
      {
        doctype: {
          type: 'checkbox',
          title: 'Document Type',
          canShow: (c) => {
            return Object.keys(c.data).length > 1;
          },
          data: {
            checklist: {
              name: 'Checklist',
              icon: getIcon('checklist'),
              filter: (cl) => cl.find((c) => get(c, 'documentableType') === 'checklist'),
            },
            step_by_step: {
              name: 'Step by Step',
              icon: getIcon('step_by_step'),
              filter: (cl) => cl.find((c) => get(c, 'documentableType') === 'step_by_step'),
            },
            interactive_document: {
              name: 'Interactive',
              icon: getIcon('interactive_document'),
              filter: (cl) => cl.find((c) => get(c, 'documentableType') === INDOC.documentableType),
            },
            sow: {
              name: 'Scope of Work',
              icon: getIcon('sow'),
              filter: (cl) => cl.scopeOfWorkId,
            },
            examples: {
              name: 'Examples',
              icon: getIcon('example'),
              filter: (cl) => cl.exampleId,
            },
            library_file: {
              name: 'Files',
              icon: getIcon('library_file'),
              filter: (cl) => cl.find((c) => get(c, 'documentableType') === FILEADD.documentableType),
            },
            library_video: {
              name: 'Videos',
              icon: getIcon('library_video'),
              filter: (cl) => cl.find((c) => get(c, 'documentableType') === VIDEOADD.documentableType),
            },
          },
        },
        system: {
          type: 'checkbox',
          title: 'System',
          filter: '0.document.classification.system.id',
          data: {},
        },
        material: {
          type: 'checkbox',
          title: 'Material',
          filter: ['0.document.classification.material.id', '0.document.classification.material.parentId'],
          data: {},
        },
      },
      vals
    );

    const d = getChecklists(true);

    const allMaterials = [];

    const selectedSystems = [];

    let datatypes = [];

    d.map((cl) => {
      const dtype = get(cl, 'documentableType');
      if (datatypes.indexOf(dtype) === -1) {
        datatypes.push(dtype);
      }

      const system = get(cl, 'document.classification.system');
      if (system) {
        const sel = ['system', 'data', system.id];
        const currentSystem = get(obj, ['system', 'data', system.id], { value: false });
        const isSel = get(currentSystem, 'value');
        if (isSel) {
          selectedSystems.push(system.id);
        }
        set(obj, sel, {
          ...system,
          ...currentSystem,
          classification: get(cl, 'document.classification'),
        });
      }
      const material = get(cl, 'document.classification.material');
      if (material) {
        allMaterials.push({ ...material, system });
        const parent = get(material, 'parent');
        if (parent) {
          allMaterials.push(parent);
        }
      }
    });

    Object.keys(obj.doctype.data).map((k) => {
      if (datatypes.indexOf(k) === -1) {
        delete obj.doctype.data[k];
      }
    });

    const tree = arrayToTree(uniq(allMaterials, 'id'), { parentProperty: 'parentId' }).filter((t) => !t.parentId && size(t.children));
    var matList = [];

    const hasInArray = (arr1, arr2) => {
      return arr1.find((a) => arr2.indexOf(a) > -1);
    };

    tree.map((mat) => {
      get(mat, 'children', []).map((material) => {
        const sel = ['material', 'data', material.id];
        const cur = get(obj, sel, { value: false });

        const allChilds = flattenAllChildren(material.children || [])
          .map((c) => get(c, 'system.id'))
          .filter((f) => f);
        const sysIds = [get(material, 'system.id'), ...allChilds];
        if (!!size(selectedSystems) && !hasInArray(selectedSystems, sysIds) && matList.indexOf(material.id) === -1) {
          delete obj['material']['data'][material.id];
          return;
        } else {
          matList.push(material.id);
        }

        set(obj, sel, {
          ...material,
          ...cur,
        });
      });
    });

    setSideFilters(obj);
  };

  const onFilterChange = (values) => {
    generateSidebar(values);
  };

  const onSearchChange = (str) => {
    setSearchString(str);
  };

  const getWallterEditionIDs = useRetrieveData(
    { url: `/wallter/edition-documents?edition_id=${libid}&query=${debouncedSearch}` },
    {
      useEffect: true,
      normalize: false,
      build: ['editionDocuments'],
      waitFor: debouncedSearch,
    }
  );

  useEffect(() => {
    if (getWallterEditionIDs && getWallterEditionIDs.loading === false) {
      setWallterResults(get(getWallterEditionIDs, 'data', []));
    }
  }, [getWallterEditionIDs]);

  useEffect(() => {
    let somethingSelected = false;
    let options = [...getChecklists()];

    const filters = {
      system: [],
      material: [],
      doctype: [],
    };

    for (let k in sideFilters) {
      const sf = sideFilters[k];
      for (let kk in sf.data) {
        const d = sf.data[kk];
        if (d.value) {
          somethingSelected = true;
          if (sf.filter) {
            filters[k].push({ filter: sf.filter, value: d.id, type: k, children: flattenAllChildren(d.children || []) });
          }
          if (d.filter) {
            filters[k].push(d.filter);
          }
        }
      }
    }

    const optPassesFilt = (opt, filt) => {
      const ff = filt.filter;
      const val = filt.value;
      if (Array.isArray(ff)) {
        return ff.filter((f) => get(opt, f) == val).length > 0;
      } else {
        return get(opt, ff) == val;
      }
    };

    const passesAnyChildren = (opt, filt) => {
      const res = optPassesFilt(opt, filt);
      if (res) {
        return res;
      }
      if (filt.children) {
        const childres = filt.children.filter((child) => {
          return optPassesFilt(child, filt);
        });
        return childres.length > 0;
      } else {
        return false;
      }
    };

    if (somethingSelected) {
      const totalFilterSize = Object.keys(filters)
        .map((k) => size(filters[k]))
        .reduce((a, item) => a + item, 0);

      options = options.filter((opt) => {
        const passObj = {
          system: [],
          material: [],
          doctype: [],
        };

        Object.keys(filters).map((k) => {
          const filts = get(filters, k);

          const passes = filts.filter((f) => {
            const hasFilter = get(f, 'filter');
            if (hasFilter) {
              return passesAnyChildren(opt, f);
            } else if (typeof f === 'function') {
              return f(opt);
            }
          });

          passObj[k] = [...passObj[k], ...passes];
        });

        const totalPass = Object.keys(passObj)
          .map((k) => size(passObj[k]))
          .reduce((a, item) => a + item, 0);

        const hasFromEachCategory = () => {
          const catsNeeded = Object.keys(filters)
            .filter((k) => size(filters[k]))
            .map((k) => k);
          const catsHave = Object.keys(passObj)
            .filter((k) => size(passObj[k]))
            .map((k) => k);
          let passesDoc = true;

          if (catsNeeded.indexOf('doctype') > -1) {
            const docPassNeed = size(get(filters, 'doctype'));
            const docpass = size(get(passObj, 'doctype'));
            passesDoc = docpass >= docPassNeed;
          }

          return passesDoc && size(catsHave.filter((f) => catsNeeded.indexOf(f) > -1)) === size(catsNeeded);
        };

        return totalPass >= totalFilterSize || hasFromEachCategory();
      });
    }

    // Filter and sort options based on search criteria and Wallter results
    if (debouncedSearch || (wallterResults && wallterResults.length > 0)) {
      options = options
        .map(optionGroup => {
          // Check if any item in the group matches the string search
          const isStringMatch = !!size(stringSearch(
            searchString, 
            optionGroup, 
            ['document.name', 'document.classification.name', 'document.classification.system.name']
          ));
          
          // Find the best (lowest) index match in Wallter results
          const wallterIndex = optionGroup.reduce((bestIndex, opt) => {
            const currentIndex = wallterResults.indexOf(String(opt.id));
            if (currentIndex === -1) return bestIndex;
            return bestIndex === -1 ? currentIndex : Math.min(bestIndex, currentIndex);
          }, -1);
          
          // Calculate priority order:
          // - Items not matching any criteria get order = -1 (will be filtered out)
          // - String matches get highest priority (order = 0)
          // - Wallter results are ordered by their position in wallterResults
          const noMatch = !isStringMatch && wallterIndex === -1;
          
          if (noMatch) {
            optionGroup.order = -1; // Will be filtered out
          } else if (isStringMatch) {
            // String matches come first (0-based), then ordered by wallterIndex if available
            optionGroup.order = wallterIndex === -1 ? 0 : wallterIndex;
          } else {
            // Pure wallter matches come after all string matches
            optionGroup.order = wallterResults.length + wallterIndex;
          }
          
          return optionGroup;
        })
        .filter(a => a.order !== -1)  // Remove groups with no matches
        .sort((a, b) => a.order - b.order);   // Sort by order priority
    }

    setData(options);
  }, [sideFilters, wallterResults, debouncedSearch, locationOn, siteContextSelectedRealm]);

  useEffect(() => {
    setData(getChecklists());
    generateSidebar(sideFilters);
  }, [editionDocuments.data,editionParentDocuments.data]);

  useEffect(() => {
    window.DOC_LIST_EDITION = editionDocuments;

    return () => {
      delete window.DOC_LIST_EDITION;
    };
  }, []);

  const getSideFilterKeys = () => {
    const dfilters = get(sideFilters, 'doctype.data', {});
    return Object.keys(dfilters)
      .filter((k) => get(dfilters, [k, 'value']))
      .map((k) => k);
  };

  useEffect(() => {
    setPrintItems(
      data
        .filter((f) => get(selected, get(f, '0.document.classification.id')))
        .map((item) => {
          const { documentParentInParentEdition } = get(item,'0',{});
          const documentParentId = showChangesOn ? get(documentParentInParentEdition,'documentableId') : undefined;
          const className = get(item, '0.document.classification.system.name');
          return {
            coverTitle: `${className}: ${get(item, '0.document.name')}`,
            render: () => {
              const docFilters = getSideFilterKeys();
              return item
                .filter((it) => {
                  if (size(docFilters)) {
                    return docFilters.indexOf(it.documentableType) > -1;
                  } else {
                    return true;
                  }
                })
                .map((doc) => <DocumentContainerWrapped subText={get(doc, 'document.classification.system.name')} printView={true} doc={doc} showLogo={true} documentParentId={documentParentId} />);
            },
          };
        })
    );
  }, [selected]);

  const onSelect = (item) => {
    if (props.isModal) {
      return;
    }
    const sel = { ...selected };
    if (get(sel, item.id)) {
      delete sel[item.id];
    } else {
      set(sel, item.id, item);
    }
    setSelected(sel);
  };

  const addAllSelected = () => {
    const sel = {};
    data.map((d) => set(sel, get(d, '0.document.classification.id'), d));
    setSelected(sel);
  };

  const filterChipDel = (filt) => {
    const { pid, id } = filt;
    const conf = { ...sideFilters };
    const location = [pid, 'data', id, 'value'];
    const currentValue = get(conf, location);
    set(conf, location, !currentValue);
    onFilterChange(conf);
  };

  const getActiveFilters = () => {
    const active = [];
    for (let k in sideFilters) {
      const sf = sideFilters[k];
      Object.keys(sf.data).map((kk) => {
        const obj = get(sf, ['data', kk], {});
        const { value } = obj;
        if (!!value) {
          active.push({ ...obj, pid: k, id: obj.id || kk });
        }
      });
    }
    return active;
  };

  const renderFilterChips = () => {
    return <FilterChips filters={getActiveFilters()} onDelete={filterChipDel} />;
  };

  const showTrans = !isDocumentPath && (editionDocuments.data || []).find((d) => size(get(d, 'document.translations')) > 1);

  const listLoading = (!!debouncedSearch && getWallterEditionIDs.loading) || editionDocuments.loading || (showChangesOn && editionParentDocuments.loading);

  return (
    <GridContainer fullWidth={isDocumentPath}>
      <LeftSideBar hidden={isMobile || isDocumentPath}>
        <LibraryFilters config={sideFilters} onChange={onFilterChange} />
      </LeftSideBar>

      <CenterContent fullWidth={isDocumentPath}>
        <div className={isDocumentPath && clsx(classes.sidebarWidth, classes.list)}>
          <h2 className={classes.title}>
            <LibraryIcon className={classes.icon} />
            Library
          </h2>
          <div className={classes.editionTitle}>
            <IconButton classes={{ root: classes.closeIcon }} component={Link} to={`/library`} {...(props.closeProps || {})} size="large">
              <Icon>close</Icon>
            </IconButton>
            <Text type="headline_medium">{editionDocuments.loading ? <Loader size={30} /> : get(edition, 'data.0.name')}</Text>
          </div>

          {isMobile ? <LibraryFilters config={sideFilters} onChange={onFilterChange} renderFilterChips={renderFilterChips} /> : renderFilterChips()}

          <ListWithSearch
            {...props}
            onSearchChange={onSearchChange}
            filterString={searchString}
            loading={listLoading}
            data={data}
            alwaysReplaceData={true}
            selected={selected}
            sideFilters={sideFilters}
            getSideFilterKeys={getSideFilterKeys}
            onSelect={onSelect}
            header={!isDocumentPath && <SelectLangEditBar edition={get(edition, 'data.0')} showTranslations={showTrans} canEdit={get(edition, 'data.0.userCanEdit', false)} canShowChanges={canShowChanges} />}
          />
        </div>

        <PrintView
          title={<>
              <div>Print Contents: {size(printItems)} Standards</div>
              {showChangesOn && (
                <Column className={clsx(classes.showChangesRow, classes.dangerBg)}>
                  {<Text type="label">{`Showing changes between ${get(edition, 'data.0.name', 'Unknown')} and ${get(editionParent, 'data.0.name', 'Unknown')}`}</Text>}
                </Column>
              )}
            </>}
          items={printItems}
          onClose={() => setSelected({})}
          button={{
            title: 'Print All',
            onClick: addAllSelected,
          }}
        />
      </CenterContent>

      <RightSideBar />
    </GridContainer>
  );
};

const ListWithSearch = withSearchBar(withInfiniteScroll(DocumentListItem, List));

export default DocumentList;
